import { type Side, type Laag, NWO_EMPLOYEE, type NwoEmployee } from '@eformulieren/model'

/**
 * Presence per kant: wat een deelnemer van de interne kant in de awareness draagt op een document
 * dat de externe kant kan lezen (R-66a, B6, B18, opdracht 8 van de reeks toegang via toekenningen).
 *
 * **Hier en niet in de pagina of in de pod**, omdat drie partijen hetzelfde moeten zeggen: de pod
 * herschrijft de awareness van zo'n deelnemer naar deze vorm (`apps/runtime/src/aanwezigheid.ts`),
 * de pagina en de cursor-extensie zetten er niets anders in (het tweede slot), en de presence-balk
 * en de cursor tonen hem. Zonder React, want de pod laadt dit subpad.
 *
 * **Een merkteken en geen naam.** De pod stuurt één vorm naar iedereen op het document (Hocuspocus
 * filtert awareness niet per ontvanger), en de lezers verschillen in taal. Dus geen tekst maar een
 * merkteken, en elke lezer zet daar het label in zijn eigen taal bij ("Medewerker van NWO", "NWO
 * staff member"). Het merkteken zelf staat in het model (`identiteit.ts`), want de pod geeft het ook
 * in zijn antwoorden. Geen kleur: die kiest de lezer ook zelf, en altijd dezelfde, zodat ze niet uit de
 * identiteit is af te leiden.
 */
export { NWO_EMPLOYEE, type NwoEmployee } from '@eformulieren/model'

/** Het label bij het merkteken, in de taal van wie kijkt. */
export const NWO_EMPLOYEE_LABEL = { nl: 'Medewerker van NWO', en: 'NWO staff member' } as const

/** Draagt deze `user` uit de awareness het merkteken? */
export function isNwoEmployee(user: unknown): boolean {
  return (
    typeof user === 'object' &&
    user !== null &&
    (user as { anoniem?: unknown }).anoniem === NWO_EMPLOYEE.anoniem
  )
}

/**
 * Kan de externe kant elk document van deze laag lezen?
 *
 * De invoerlaag: daar schrijft de aanvrager, en hij leest haar in elke beurt van zijn dossier
 * (`rolRecht` in de engine). Op zo'n document verschijnt een deelnemer van de interne kant dus
 * altijd als {@link NWO_EMPLOYEE}, in de pod en in de pagina.
 *
 * **De toetslaag niet altijd, maar ook niet nooit.** Een invuller krijgt geen toetssectie, en een
 * persoon van de externe ingang kan geen toetser zijn (R-155a). Maar een toetser kan een
 * toetssectie met leesrecht delen (R-65b, R-184), en wie die deling verzilvert krijgt de kant van
 * zijn eigen ingang: een adviseur van buiten leest dan het toetsdocument. Dat weet alleen de pod,
 * op het moment dat zo iemand verbindt, en daar wordt het ook afgedwongen; de pagina kan het niet
 * weten en zet op een toetsdocument de naam erin.
 */
export function alwaysExternalReadable(laag: Laag): boolean {
  return laag === 'invoer'
}

/**
 * Wat een client over zichzelf als `user` in de awareness zet: het tweede slot naast de pod.
 *
 * Een deelnemer van de interne kant op een document dat de externe kant altijd kan lezen, zet zijn
 * persoon er zelf al niet in. Op een toetsdocument wel: of daar iemand van buiten meeleest weet
 * alleen de pod, en die dwingt het daar af.
 */
export function ownPresence(person: string, side: Side, laag: Laag): NwoEmployee | { readonly name: string } {
  return side === 'intern' && alwaysExternalReadable(laag) ? { ...NWO_EMPLOYEE } : { name: person }
}
